import React, { useEffect, useState } from "react";
import Spinner from "../components/Spinner";
import TableWrapper from "../components/TableWrapper";

export default function MatchStandings({ leagueId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playingTeams, setPlayingTeams] = useState([]);
  const season = 2026;

  useEffect(() => {
    if (!leagueId) return;
    let mounted = true;
    const fetchStandings = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/standings/get?leagueId=${leagueId}&season=${season}`,
          {
            headers: {
              "Content-Type": "application/json",
              key: process.env.REACT_APP_ACCESKEY,
            },
          },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const list = Array.isArray(json) ? json : (json.response ?? json ?? []);
        if (mounted) setData(list);

        extractPlayingTeamsFromPage();
      } catch (err) {
        console.error(err);
        if (mounted) setError("Failed to load standings.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    const extractPlayingTeamsFromPage = () => {
      try {
        const headings = document.querySelectorAll("h1, h2, h3, h4, h5, h6");

        for (let heading of headings) {
          const text = heading.textContent.trim();
          if (text.includes("vs") && text.length > 10) {
            const teams = text.split("vs").map((team) => team.trim());
            if (teams.length === 2 && teams[0] && teams[1]) {
              setPlayingTeams([teams[0], teams[1]]);
              console.log("Extracted teams from heading:", teams);
              return;
            }
          }
        }

        const tabButtons = document.querySelectorAll(
          'button, .tab, [role="tab"]',
        );
        for (let tab of tabButtons) {
          const text = tab.textContent.trim();
          if (text.includes("vs") && text.length > 10) {
            const teams = text.split("vs").map((team) => team.trim());
            if (teams.length === 2 && teams[0] && teams[1]) {
              setPlayingTeams([teams[0], teams[1]]);
              console.log("Extracted teams from tab:", teams);
              return;
            }
          }
        }
      } catch (err) {
        console.error("Error extracting teams from page:", err);
      }
    };

    fetchStandings();
    return () => {
      mounted = false;
    };
  }, [leagueId, season]);

  const isPlayingTeam = (teamName) => {
    if (!teamName || playingTeams.length === 0) return false;

    const teamNameTrimmed = teamName.trim();

    return playingTeams.some(
      (playingTeam) => teamNameTrimmed === playingTeam.trim(),
    );
  };

  if (loading) return <Spinner text="Loading standings..." />;
  if (error)
    return (
      <div className="p-4 bg-red-950/30 text-red-400 rounded-lg border border-red-900/50 text-sm">
        {error}
      </div>
    );
  if (!data?.length)
    return (
      <div className="text-gray-500 p-4 text-sm">No standings available.</div>
    );

  const parseForm = (form) => {
    if (!form) return [];
    if (Array.isArray(form)) return form.slice(0, 6);
    const cleaned = String(form).replace(/[, ]+/g, "");
    return cleaned.split("").slice(0, 6);
  };

  const FormBadge = ({ v }) => {
    const s = String(v).toUpperCase();
    const base =
      "inline-flex items-center justify-center w-6 h-5 text-xs font-semibold rounded mr-1";
    const classes =
      s === "W"
        ? `${base} bg-emerald-600/90 text-white`
        : s === "D"
          ? `${base} bg-gray-500/90 text-white`
          : s === "L"
            ? `${base} bg-red-600/90 text-white`
            : `${base} bg-gray-600/90 text-white`;
    return <span className={classes}>{s}</span>;
  };

  const tableHeaderClass =
    "text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-700";
  const tableCellClass =
    "py-3 px-4 text-sm text-gray-400 border-b border-gray-800";
  const tableCellCenterClass =
    "py-3 px-4 text-sm text-gray-400 text-center border-b border-gray-800";

  return (
    <div className="mt-4">
      {/* Desktop View - Hidden on mobile */}
      <div className="hidden md:block">
        <TableWrapper titleColor="text-green-400" title="League Standings">
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead>
                <tr>
                  <th className={tableHeaderClass}>#</th>
                  <th className={tableHeaderClass}>Team</th>
                  <th className={tableHeaderClass + " text-center"}>Pts</th>
                  <th className={tableHeaderClass}>Form</th>
                  <th className={tableHeaderClass + " text-center"}>Pl</th>
                  <th className={tableHeaderClass + " text-center"}>W</th>
                  <th className={tableHeaderClass + " text-center"}>D</th>
                  <th className={tableHeaderClass + " text-center"}>L</th>
                  <th className={tableHeaderClass + " text-center"}>GD</th>
                  <th className={tableHeaderClass + " text-center"}>GF</th>
                  <th className={tableHeaderClass + " text-center"}>GA</th>
                  <th className={tableHeaderClass + " text-center"}>Home</th>
                  <th className={tableHeaderClass + " text-center"}>Away</th>
                </tr>
              </thead>
              <tbody>
                {data.map((team) => {
                  const formArr = parseForm(
                    team.form ?? team.formatted_form ?? team.recent_form ?? "",
                  );
                  const isPlaying = isPlayingTeam(team.team_name ?? team.name);

                  return (
                    <tr
                      key={team.team_id ?? team.team_name}
                      className={`hover:bg-gray-700/40 transition-colors ${
                        isPlaying
                          ? "bg-green-300/20 border-l-4 border-l-green-500"
                          : ""
                      }`}
                    >
                      <td
                        className={`${tableCellCenterClass} font-medium ${isPlaying ? "text-green-500" : "text-gray-400"}`}
                      >
                        {team.rank ?? team.position ?? "-"}
                      </td>

                      <td className={tableCellClass}>
                        <div className="flex items-center gap-3">
                          {team.team_logo ? (
                            <img
                              src={team.team_logo}
                              alt={team.team_name}
                              className="w-6 h-6 object-contain flex-shrink-0"
                            />
                          ) : (
                            <div className="w-6 h-6 bg-gray-800 rounded flex-shrink-0" />
                          )}
                          <div>
                            <div
                              className={`text-sm font-medium ${isPlaying ? "text-green-500" : "text-gray-300"}`}
                            >
                              {team.team_name ?? "-"}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td
                        className={`${tableCellCenterClass} font-bold ${isPlaying ? "text-green-500" : "text-gray-300"}`}
                      >
                        {team.points ?? "-"}
                      </td>

                      <td className={tableCellClass}>
                        <div className="flex items-center">
                          {formArr.length ? (
                            formArr.map((f, i) => <FormBadge key={i} v={f} />)
                          ) : (
                            <span className="text-xs text-gray-600">—</span>
                          )}
                        </div>
                      </td>

                      <td className={tableCellCenterClass}>
                        {team.played ?? team.matches ?? "-"}
                      </td>
                      <td className={tableCellCenterClass}>
                        {team.win ?? "-"}
                      </td>
                      <td className={tableCellCenterClass}>
                        {team.draw ?? "-"}
                      </td>
                      <td className={tableCellCenterClass}>
                        {team.lose ?? team.loss ?? "-"}
                      </td>
                      <td className={tableCellCenterClass}>
                        {team.goals_diff ?? team.goal_diff ?? "-"}
                      </td>
                      <td className={tableCellCenterClass}>
                        {team.goals_for ?? team.for ?? "-"}
                      </td>
                      <td className={tableCellCenterClass}>
                        {team.goals_against ?? team.against ?? "-"}
                      </td>

                      <td className={tableCellCenterClass}>
                        <div className="text-xs text-gray-400">
                          <span className="text-green-500">
                            {team.home_win ?? team.home_w ?? "-"}
                          </span>
                          <span className="text-gray-600"> / </span>
                          <span className="text-gray-400">
                            {team.home_draw ?? team.home_d ?? "-"}
                          </span>
                          <span className="text-gray-600"> / </span>
                          <span className="text-red-500">
                            {team.home_lose ?? team.home_l ?? "-"}
                          </span>
                        </div>
                      </td>

                      <td className={tableCellCenterClass}>
                        <div className="text-xs text-gray-400">
                          <span className="text-green-500">
                            {team.away_win ?? team.away_w ?? "-"}
                          </span>
                          <span className="text-gray-600"> / </span>
                          <span className="text-gray-400">
                            {team.away_draw ?? team.away_d ?? "-"}
                          </span>
                          <span className="text-gray-600"> / </span>
                          <span className="text-red-500">
                            {team.away_lose ?? team.away_l ?? "-"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TableWrapper>
      </div>

      {/* Mobile View - MODIFICAT */}
      <div className="md:hidden space-y-4">
        <TableWrapper titleColor="text-green-400" title="League Standings">
          <div className="space-y-2">
            {data.map((team) => {
              const formArr = parseForm(
                team.form ?? team.formatted_form ?? team.recent_form ?? "",
              );
              const isPlaying = isPlayingTeam(team.team_name ?? team.name);

              return (
                <div
                  key={team.team_id ?? team.team_name}
                  className={`bg-gray-800/50 rounded-lg p-3 border ${
                    isPlaying ? "border-green-500/50" : "border-gray-700/50"
                  }`}
                >
                  {/* Header cu poziție, echipă, puncte - LEAGUE STANDINGS CENTRAT */}
                  <div className="flex items-center gap-2 mb-3">
                    <div
                      className={`w-7 text-center font-bold text-sm ${isPlaying ? "text-green-500" : "text-gray-400"}`}
                    >
                      {team.rank ?? team.position ?? "-"}
                    </div>
                    {team.team_logo ? (
                      <img
                        src={team.team_logo}
                        alt={team.team_name}
                        className="w-6 h-6 object-contain rounded"
                      />
                    ) : (
                      <div className="w-6 h-6 bg-gray-800 rounded" />
                    )}
                    <div className="flex-1 text-center">
                      <div
                        className={`text-sm font-medium truncate ${isPlaying ? "text-green-500" : "text-gray-300"}`}
                      >
                        {team.team_name ?? "-"}
                      </div>
                    </div>
                    <div
                      className={`text-base font-bold ${isPlaying ? "text-green-500" : "text-gray-300"}`}
                    >
                      {team.points ?? "-"}
                    </div>
                  </div>

                  {/* Statistici - Pl, W, D, L, GF, GA toate pe aceeași linie - fără culori */}
                  <div className="grid grid-cols-6 gap-1 mb-3 text-center">
                    <div className="bg-gray-900/50 rounded p-1">
                      <div className="text-[10px] text-gray-500">Pl</div>
                      <div className="text-xs text-gray-300">
                        {team.played ?? team.matches ?? "-"}
                      </div>
                    </div>
                    <div className="bg-gray-900/50 rounded p-1">
                      <div className="text-[10px] text-gray-500">W</div>
                      <div className="text-xs text-gray-300">
                        {team.win ?? "-"}
                      </div>
                    </div>
                    <div className="bg-gray-900/50 rounded p-1">
                      <div className="text-[10px] text-gray-500">D</div>
                      <div className="text-xs text-gray-300">
                        {team.draw ?? "-"}
                      </div>
                    </div>
                    <div className="bg-gray-900/50 rounded p-1">
                      <div className="text-[10px] text-gray-500">L</div>
                      <div className="text-xs text-gray-300">
                        {team.lose ?? team.loss ?? "-"}
                      </div>
                    </div>
                    <div className="bg-gray-900/50 rounded p-1">
                      <div className="text-[10px] text-gray-500">GF</div>
                      <div className="text-xs text-gray-300">
                        {team.goals_for ?? team.for ?? "-"}
                      </div>
                    </div>
                    <div className="bg-gray-900/50 rounded p-1">
                      <div className="text-[10px] text-gray-500">GA</div>
                      <div className="text-xs text-gray-300">
                        {team.goals_against ?? team.against ?? "-"}
                      </div>
                    </div>
                  </div>

                  {/* Forma - mai lată */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-500 w-10">Form:</span>
                    <div className="flex items-center justify-center flex-1">
                      {formArr.length ? (
                        <div className="flex items-center gap-1">
                          {formArr.map((f, i) => (
                            <FormBadge key={i} v={f} />
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-600">—</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </TableWrapper>
      </div>
    </div>
  );
}
